<template>
  <component :is="props.embedded ? 'div' : AuthenticatedView" v-bind="authenticatedViewProps">
    <AppHeader
      v-if="currentUser && !props.embedded"
      :user="currentUser"
      :chat-id="chatId"
      :from-id="fromId"
      dashboard-url="/dashboard"
      profile-url="/profile"
    />
    <div :class="props.embedded ? 'embedded-task-create-container' : 'card'">
      <breadcrumbs v-if="!props.embedded" :items="breadcrumbsItems" />
      <archived-project-warning v-if="!loading && projectArchived" :archived="projectArchived" />
      <h2 v-if="!props.embedded" style="margin-top: 0">{{ s__('TaskForm|New Task') }}</h2>
      <div v-if="loading">{{ __('Loading…') }}</div>
      <div v-else>
        <project-tasks-link
          v-if="project && !props.embedded"
          :project-id="project.id"
          :chat-id="chatId"
          :from-id="fromId"
        />
        <div v-if="props.embedded" class="dashboard-task-create-project-row">
          <strong>{{ s__('TaskForm|Project') }}:</strong>
          <span>{{ props.embeddedProjectTitle || project?.title }}</span>
          <div
            v-if="!isCompactView && showTemplateSelector"
            class="template-picker template-picker-embedded"
          >
            <button
              type="button"
              class="template-picker-button"
              :class="{ 'template-picker-button-active': selectedTemplateId }"
              :aria-expanded="showTemplatePicker"
              :aria-label="s__('TaskForm|Use Template')"
              :title="s__('TaskForm|Use Template')"
              @click="showTemplatePicker = !showTemplatePicker"
            >
              <TaskTemplateIcon />
            </button>
            <div v-if="showTemplatePicker" class="template-picker-popover">
              <label class="template-picker-label">{{ s__('TaskForm|Use Template') }}</label>
              <select
                v-model="selectedTemplateId"
                :aria-label="s__('TaskForm|Use Template')"
                @change="handleTemplateChange"
              >
                <option :value="null">{{ s__('TaskForm|None') }}</option>
                <optgroup
                  v-if="projectTemplates.length > 0"
                  :label="s__('TaskForm|Project Templates')"
                >
                  <option
                    v-for="template in projectTemplates"
                    :key="template.id"
                    :value="template.id"
                  >
                    {{ template.taskTitle || template.task_title }}
                  </option>
                </optgroup>
                <optgroup
                  v-if="userTemplates.length > 0"
                  :label="s__('TaskForm|Personal Templates')"
                >
                  <option v-for="template in userTemplates" :key="template.id" :value="template.id">
                    {{ template.taskTitle || template.task_title }}
                  </option>
                </optgroup>
              </select>
            </div>
          </div>
          <button
            v-if="props.canGoBackToProjectSelection"
            type="button"
            class="btn-secondary btn-sm"
            @click="emit('back-to-project-selection')"
          >
            {{ __('Back') }}
          </button>
        </div>
        <!-- Compact View -->
        <div v-if="isCompactView" class="task-info-container">
          <div class="row">
            <div style="flex: 1">
              <label>{{ s__('TaskForm|Title') }} *</label>
              <SingleLineTextarea
                v-model="form.title"
                class="task-title-input"
                :placeholder="s__('TaskForm|Enter task title')"
              />
            </div>
          </div>
          <div v-if="!isPersonalProject" class="row">
            <div style="flex: 1">
              <label>{{ s__('TaskForm|Assignee') }}<span v-if="isAssigneeRequired"> *</span></label>
              <user-select
                v-model="form.assignee_id"
                :users="members"
                :placeholder="s__('TaskForm|Unassigned')"
                :searchable="true"
                :project-id="project?.id"
              />
            </div>
          </div>
          <div v-if="isPriorityRequired" class="row">
            <div style="flex: 1">
              <label>{{ __('Priority') }}<span> *</span></label>
              <select v-model="form.priority">
                <option :value="undefined">{{ s__('TaskForm|None') }}</option>
                <option v-for="p in sortedPriorities" :key="p.value" :value="p.value">
                  {{ translatePriority(p.value) }}
                </option>
              </select>
            </div>
          </div>
          <div class="row">
            <div style="flex: 1">
              <label>{{ s__('TaskForm|Due') }}<span v-if="isDueAtRequired"> *</span></label>
              <DateTimeInput
                :datetime="form.due_at"
                :title="s__('TaskForm|Due')"
                @update:datetime="form.due_at = $event"
              />
            </div>
          </div>
          <div class="row task-form-actions" style="gap: 8px">
            <button :disabled="submitting || !canSubmit" class="btn-primary" @click="submit">
              {{ s__('TaskForm|Create Task') }}
            </button>
            <button class="btn-secondary" @click="isCompactView = false">
              {{ s__('TaskForm|More options') }}
            </button>
          </div>
          <div v-if="error" class="error">{{ error }}</div>
          <div v-if="success" class="success">{{ s__('TaskForm|Task created.') }}</div>
        </div>

        <!-- Full View -->
        <div v-else class="task-info-container">
          <div v-if="!props.embedded" class="row">
            <div style="flex: 1">
              <label>{{ s__('TaskForm|Project') }}</label>
              <div class="muted">{{ project ? project.title : s__('TaskForm|Unknown') }}</div>
            </div>
          </div>
          <!-- Task Template Selector -->
          <div v-if="!props.embedded && showTemplateSelector" class="template-picker">
            <button
              type="button"
              class="template-picker-button"
              :class="{ 'template-picker-button-active': selectedTemplateId }"
              :aria-expanded="showTemplatePicker"
              :aria-label="s__('TaskForm|Use Template')"
              :title="s__('TaskForm|Use Template')"
              @click="showTemplatePicker = !showTemplatePicker"
            >
              <TaskTemplateIcon />
            </button>
            <div v-if="showTemplatePicker" class="template-picker-popover">
              <label class="template-picker-label">{{ s__('TaskForm|Use Template') }}</label>
              <select
                v-model="selectedTemplateId"
                :aria-label="s__('TaskForm|Use Template')"
                @change="handleTemplateChange"
              >
                <option :value="null">{{ s__('TaskForm|None') }}</option>
                <optgroup
                  v-if="projectTemplates.length > 0"
                  :label="s__('TaskForm|Project Templates')"
                >
                  <option
                    v-for="template in projectTemplates"
                    :key="template.id"
                    :value="template.id"
                  >
                    {{ template.taskTitle || template.task_title }}
                  </option>
                </optgroup>
                <optgroup
                  v-if="userTemplates.length > 0"
                  :label="s__('TaskForm|Personal Templates')"
                >
                  <option v-for="template in userTemplates" :key="template.id" :value="template.id">
                    {{ template.taskTitle || template.task_title }}
                  </option>
                </optgroup>
              </select>
            </div>
          </div>
          <div class="row">
            <div style="flex: 1">
              <label>{{ s__('TaskForm|Title') }}</label>
              <SingleLineTextarea
                v-model="form.title"
                class="task-title-input"
                :placeholder="s__('TaskForm|Enter task title')"
              />
            </div>
          </div>
          <div v-if="!isPersonalProject" class="row">
            <div style="flex: 1">
              <label>{{ s__('TaskForm|Assignee') }}<span v-if="isAssigneeRequired"> *</span></label>
              <user-select
                v-model="form.assignee_id"
                :users="members"
                :placeholder="s__('TaskForm|Unassigned')"
                :searchable="true"
              />
            </div>
          </div>
          <div class="row">
            <div style="flex: 1">
              <label>{{ s__('TaskForm|Due') }}<span v-if="isDueAtRequired"> *</span></label>
              <DateTimeInput
                :datetime="form.due_at"
                :title="s__('TaskForm|Due')"
                @update:datetime="form.due_at = $event"
              />
              <div v-if="isDueAtRequired" class="muted">{{ s__('TaskForm|Required') }}</div>
              <div v-else class="muted">{{ s__('TaskForm|Optional') }}</div>
            </div>
          </div>
          <div class="row">
            <div style="flex: 1">
              <label>{{ s__('TaskForm|Category') }}</label>
              <select v-model="form.task_category_id">
                <option v-for="tt in taskCategories" :key="tt.id" :value="tt.id">
                  {{ tt.name }}
                </option>
              </select>
              <div v-if="taskCategories.length === 0" class="muted">
                {{
                  s__(
                    'TaskForm|No task categories defined — a default will be created server-side.'
                  )
                }}
              </div>
            </div>
            <div style="flex: 1">
              <label>{{ s__('TaskForm|Status') }}</label>
              <select v-if="workflowStatuses.length > 0" v-model="form.workflow_status_id">
                <option v-for="status in workflowStatuses" :key="status.id" :value="status.id">
                  {{ status.name }}{{ status.isInitial ? ` (${s__('TaskForm|Initial')})` : '' }}
                </option>
              </select>
              <div v-else class="muted">{{ s__('TaskForm|No workflow configured') }}</div>
            </div>
          </div>
          <div class="row">
            <div style="flex: 1">
              <label>{{ __('Priority') }}<span v-if="isPriorityRequired"> *</span></label>
              <select v-model="form.priority">
                <option :value="undefined">{{ s__('TaskForm|None') }}</option>
                <option v-for="p in sortedPriorities" :key="p.value" :value="p.value">
                  {{ translatePriority(p.value) }}
                </option>
              </select>
              <div v-if="!isPriorityRequired" class="muted">{{ s__('TaskForm|Optional') }}</div>
              <div v-else class="muted">{{ s__('TaskForm|Required') }}</div>
            </div>
            <div v-if="verticalRelationsEnabled" style="flex: 1">
              <label>{{ s__('TaskForm|Parent Task') }}</label>
              <task-select
                v-model="form.parent_task_id"
                :project-id="project?.id"
                :placeholder="s__('TaskForm|No parent task')"
                :search-placeholder="s__('TaskForm|Search tasks...')"
              />
              <div class="muted">{{ s__('TaskForm|Optional') }}</div>
            </div>
          </div>
          <div class="row">
            <div style="flex: 1">
              <label>{{ s__('TaskForm|Description') }}</label>
              <TelegramDualEditor
                v-model="form.description"
                :members="members"
                :project-id="project?.id || projectId"
                :placeholder="s__('TaskForm|Optional description')"
                :enable-wysiwyg="true"
                :clipboard-upload-enabled="s3AttachmentsEnabled"
                :attachment-context="taskDescriptionAttachmentContext"
                :attachment-draft-id="attachmentDraftId"
                min-height="200px"
                @draft-created="attachmentDraftId = $event"
              />
              <div style="display: flex; gap: 8px; margin-top: 8px">
                <button
                  class="btn-sm btn-secondary"
                  :disabled="!form.description.trim()"
                  @click="showDescriptionPreview"
                >
                  {{ s__('TaskForm|Preview') }}
                </button>
              </div>
            </div>
          </div>
          <div v-if="s3AttachmentsEnabled" class="row">
            <div style="flex: 1">
              <DraftAttachmentUploader
                v-model:attachment-draft-id="attachmentDraftId"
                v-model:attachments="draftAttachments"
                :project-id="project?.id || projectId"
                purpose="task_attachments"
                :label="s__('Attachments|Attachments')"
                :drop-text="s__('Attachments|Drop files here or click to upload')"
                :size-text="s__('Attachments|Maximum file size: 100 MB')"
                :uploading-text="s__('Attachments|Uploading...')"
                :upload-error-text="s__('Attachments|Failed to upload attachments')"
                :delete-error-text="s__('Attachments|Failed to delete attachment')"
                :delete-title="s__('Attachments|Delete attachment')"
              />
            </div>
          </div>
          <div v-if="s3AttachmentsEnabled && availableProjectAttachments.length" class="row">
            <div style="flex: 1">
              <ProjectAttachmentPicker
                :available-attachments="availableProjectAttachments"
                :selected-ids="selectedExistingAttachmentIds"
                :label="s__('Attachments|Attach existing project file')"
                :placeholder="s__('Attachments|Select a file…')"
                :delete-title="s__('Attachments|Delete attachment')"
                select-id="existing-project-attachment-select"
                @update:selected-ids="selectedExistingAttachmentIds = $event"
              />
            </div>
          </div>
          <!-- Project Labels -->
          <div v-if="labels.length > 0 || canManageLabels" class="row labels-row">
            <OnTaskCreateLabelsForm
              scope="project"
              :project-id="project?.id"
              :selected-label-ids="form.label_ids"
              :available-labels="labels"
              :can-create-label="canManageLabels"
              :label-text="
                personalLabelsEnabled
                  ? s__('TaskForm|Project Labels (optional)')
                  : s__('TaskForm|Labels (optional)')
              "
              :empty-message="s__('TaskForm|No labels defined yet.')"
              :create-button-text="__('Create')"
              :create-modal-title="s__('TaskForm|Create Label')"
              @update:selected-label-ids="form.label_ids = $event"
              @update:available-labels="labels = $event"
              @refetch="refetchProjectLabels"
            />
          </div>

          <!-- Personal Labels -->
          <div v-if="personalLabelsEnabled" class="row labels-row">
            <OnTaskCreateLabelsForm
              scope="personal"
              :selected-label-ids="form.personal_label_ids"
              :available-labels="personalLabels"
              :can-create-label="true"
              :label-text="s__('TaskForm|Personal Labels (optional)')"
              :empty-message="s__('TaskForm|No personal labels defined yet.')"
              :create-button-text="__('Create')"
              :create-modal-title="s__('TaskForm|Create Personal Label')"
              @update:selected-label-ids="form.personal_label_ids = $event"
              @update:available-labels="personalLabels = $event"
              @refetch="refetchPersonalLabels"
            />
          </div>
          <!-- Checklist (only if feature is enabled) -->
          <div v-if="checklistsEnabled" class="row">
            <div style="flex: 1">
              <label>{{ s__('TaskForm|Checklist') }}</label>
              <div class="checklist-items-form">
                <div
                  v-for="(item, index) in checklistForm.items"
                  :key="index"
                  class="checklist-item-row"
                  style="display: flex; gap: 8px; align-items: center; margin-bottom: 4px"
                >
                  <span style="color: #757575; font-size: 0.9rem; min-width: 24px"
                    >{{ index + 1 }}.</span
                  >
                  <input
                    v-model="checklistForm.items[index].title"
                    type="text"
                    :placeholder="s__('TaskForm|Item title')"
                    style="flex: 1"
                  />
                  <button
                    type="button"
                    class="btn-icon-small"
                    :title="s__('TaskForm|Remove item')"
                    @click="removeChecklistItem(index)"
                  >
                    <TrashIcon />
                  </button>
                </div>
                <div style="display: flex; gap: 8px; align-items: center; margin-top: 8px">
                  <SingleLineTextarea
                    ref="newChecklistItemInput"
                    v-model="newChecklistItemTitle"
                    :rows-desktop="1"
                    :rows-mobile="2"
                    :placeholder="s__('TaskForm|Add checklist item')"
                    style="flex: 1"
                    @keydown.enter="addChecklistItem"
                    @keydown.tab="handleChecklistItemTab"
                  />
                  <button
                    type="button"
                    class="btn-sm"
                    :disabled="!newChecklistItemTitle.trim()"
                    @click="addChecklistItem"
                  >
                    {{ s__('TaskForm|Add') }}
                  </button>
                </div>
              </div>
              <div class="muted">
                {{ s__('TaskForm|Optional: Add a checklist to track task completion steps') }}
              </div>
            </div>
          </div>
          <div v-if="confidentialTasksEnabled && !isPersonalProject" class="row">
            <div style="flex: 1">
              <label>
                <input v-model="form.confidential" type="checkbox" style="margin-right: 8px" />
                {{ s__('TaskForm|Confidential?') }}
              </label>
              <div class="muted">
                {{
                  s__(
                    'TaskForm|Only visible to author, assignee, project owners, and whitelisted viewers'
                  )
                }}
              </div>
            </div>
          </div>
          <div
            v-if="confidentialTasksEnabled && !isPersonalProject && form.confidential"
            class="row"
          >
            <div style="flex: 1">
              <label>{{ s__('TaskForm|Whitelisted Viewers') }}</label>
              <select
                v-model="form.whitelisted_viewer_ids"
                multiple
                size="4"
                style="width: 100%; padding: 8px; border: 1px solid #ddd; border-radius: 4px"
              >
                <option v-for="member in members" :key="member.id" :value="member.id">
                  {{ formatUserForSelect(member) }}
                </option>
              </select>
              <div class="muted">
                {{ s__('TaskForm|Select users who can view this confidential task') }}
              </div>
            </div>
          </div>
          <div class="row task-form-actions" style="gap: 8px">
            <button :disabled="submitting || !canSubmit" class="btn-primary" @click="submit">
              {{ s__('TaskForm|Create') }}
            </button>
            <button class="btn-secondary" @click="isCompactView = true">
              {{ s__('TaskForm|Use compact form') }}
            </button>
          </div>
          <div v-if="error" class="error">{{ error }}</div>
          <div v-if="success" class="success">{{ s__('TaskForm|Task created.') }}</div>

          <!-- Tip about preference -->
          <div class="form-tip">
            💡
            {{
              s__('TaskForm|Tip: You can set your preferred form view (compact or full) in your')
            }}
            <a :href="profileUrl" class="tip-link">{{ s__('TaskForm|Profile settings') }}</a>
          </div>
        </div>
      </div>

      <!-- Description Preview Modal -->
      <Teleport to="body">
        <div v-if="showDescriptionPreviewModal" class="modal-overlay">
          <div class="modal-content" style="max-width: 600px">
            <div class="modal-header">
              <h3>{{ s__('TaskForm|Description Preview') }}</h3>
              <button
                class="modal-close"
                aria-label="Close"
                @click="showDescriptionPreviewModal = false"
              >
                ×
              </button>
            </div>
            <div class="modal-body">
              <div class="preview-content rich-content-read rich-content-preview">
                <div v-html="descriptionPreviewContent"></div>
                <div
                  v-if="!descriptionPreviewContent"
                  class="muted"
                  style="color: #9e9e9e; font-style: italic"
                >
                  {{ s__('TaskForm|No content to preview') }}
                </div>
              </div>
              <div style="margin-top: 16px; text-align: right">
                <button class="btn-sm btn-secondary" @click="showDescriptionPreviewModal = false">
                  {{ s__('TaskForm|Close') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Teleport>
    </div>
  </component>
</template>

<script setup>
import { ref, onMounted, computed, watch, nextTick } from 'vue'
import { useQuery, useMutation, useApolloClient, useLazyQuery } from '@vue/apollo-composable'
import { gql } from '@apollo/client/core'
import ProjectTasksLink from './ProjectTasksLink.vue'
import DateTimeInput from '../../common/DateTimeInput.vue'
import SingleLineTextarea from '../../common/SingleLineTextarea.vue'
import TelegramDualEditor from '../../common/TelegramDualEditor.vue'
import UserSelect from '../../common/UserSelect.vue'
import TaskSelect from '../../common/TaskSelect.vue'
import DraftAttachmentUploader from '../../common/DraftAttachmentUploader.vue'
import ProjectAttachmentPicker from '../../common/ProjectAttachmentPicker.vue'
import TrashIcon from '../../common/icons/TrashIcon.vue'
import TaskTemplateIcon from '../../common/icons/TaskTemplateIcon.vue'
import { s__, __, translatePriority, loadTranslations } from '../../../locale/index.js'
import { telegramMarkdownToHtml } from '../../../utils/telegramEditorCodec.js'
import { extractTaskMentions } from '../../../utils/taskMentions.js'
import { fetchMentionedTasks } from '../../../utils/fetchMentionedTasks.js'
import Breadcrumbs from '../../common/Breadcrumbs.vue'
import ArchivedProjectWarning from '../../common/ArchivedProjectWarning.vue'
import OnTaskCreateLabelsForm from '../../common/OnTaskCreateLabelsForm.vue'
import AppHeader from '../../common/AppHeader.vue'
import AuthenticatedView from '../../common/AuthenticatedView.vue'
import { removeInitialLoading } from '@/utils/loadingScreen'

const props = defineProps({
  projectId: String,
  chatId: String,
  fromId: String,
  embedded: {
    type: Boolean,
    default: false,
  },
  embeddedProjectTitle: {
    type: String,
    default: '',
  },
  canGoBackToProjectSelection: {
    type: Boolean,
    default: false,
  },
})
const emit = defineEmits(['task-created', 'back-to-project-selection'])

const authenticatedViewProps = computed(() =>
  props.embedded
    ? {}
    : {
        loading: loading.value && !currentUserResult.value,
        currentUser: currentUser.value,
        hasResult: !!currentUserResult.value,
      }
)
const projectId = props.projectId
const chatId = props.chatId
const fromId = props.fromId

// Locale key to force re-render when translations are loaded
const localeKey = ref(0)

const loading = ref(true)
const error = ref('')
const success = ref(false)
const submitting = ref(false)
const project = ref(null)
const taskCategories = ref([])
const labels = ref([])
const members = ref([])
const priorities = ref([])
// Sort priorities so higher priorities appear first
const sortedPriorities = computed(() => {
  return [...priorities.value].reverse()
})
const workflowStatuses = ref([])
const isCompactView = ref(false)
const confidentialTasksEnabled = ref(false)
const isPersonalProject = ref(false)
const projectArchived = ref(false)
const projectOwnerId = ref(null)
const canManageLabels = ref(false)
const verticalRelationsEnabled = ref(false)
const personalLabelsEnabled = ref(false)
const personalLabels = ref([])
const showDescriptionPreviewModal = ref(false)
const descriptionPreviewContent = ref('')
const userTemplatesEnabled = ref(false)
const projectTemplatesEnabled = ref(false)
const userTemplates = ref([])
const projectTemplates = ref([])
const selectedTemplateId = ref(null)
const showTemplatePicker = ref(false)
const attachmentDraftId = ref(null)
const draftAttachments = ref([])
const availableProjectAttachments = ref([])
const selectedExistingAttachmentIds = ref([])
const checklistsEnabled = ref(false)
const requiredFields = ref([])
const checklistForm = ref({
  items: [],
})
const newChecklistItemTitle = ref('')
const newChecklistItemInput = ref(null)

// Parse query parameters from URL
function parseQueryParams() {
  const urlParams = new URLSearchParams(window.location.search)
  const parsed = {
    parent_task_id: urlParams.get('parent_task_id') || undefined,
    title: urlParams.get('title') || undefined,
    assignee_id: urlParams.get('assignee_id') || undefined,
    due_at: urlParams.get('due_at') || undefined,
    task_category_id: urlParams.get('task_category_id') || undefined,
  }
  return parsed
}

// Parse due_at ISO string into date, hour, minute
function parseDueAt(isoString) {
  if (!isoString) return { date: '', hour: '00', minute: '00' }
  try {
    const date = new Date(isoString)
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const hour = String(date.getHours()).padStart(2, '0')
    const minute = String(date.getMinutes()).padStart(2, '0')
    return {
      date: `${year}-${month}-${day}`,
      hour: hour,
      minute: minute,
    }
  } catch {
    return { date: '', hour: '00', minute: '00' }
  }
}

const queryParams = parseQueryParams()
const parsedDueAt = parseDueAt(queryParams.due_at)

const form = ref({
  title: queryParams.title || '',
  description: '',
  task_category_id: queryParams.task_category_id || null,
  label_ids: [],
  personal_label_ids: [],
  priority: undefined,
  due_at: queryParams.due_at || null,
  assignee_id: queryParams.assignee_id || undefined,
  workflow_status_id: undefined,
  confidential: false,
  whitelisted_viewer_ids: [],
  parent_task_id: queryParams.parent_task_id || undefined,
  checklist_data: null,
})

const s3AttachmentsEnabled = computed(() => window.gon?.s3_attachments_enabled === true)
const taskDescriptionAttachmentContext = computed(() => ({
  projectId: project.value?.id || projectId,
  purpose: 'task_description',
}))

const isPriorityRequired = computed(() => {
  return !isPersonalProject.value && requiredFields.value.includes('priority')
})

const isAssigneeRequired = computed(() => {
  return !isPersonalProject.value && requiredFields.value.includes('assignee_id')
})

const isDueAtRequired = computed(() => {
  return !isPersonalProject.value && requiredFields.value.includes('due_at')
})

const canSubmit = computed(() => {
  if (form.value.title.trim().length === 0) return false
  if (!form.value.task_category_id && taskCategories.value.length > 0) return false
  if (isPriorityRequired.value && !form.value.priority) return false
  if (isAssigneeRequired.value && !form.value.assignee_id) return false
  if (isDueAtRequired.value && !form.value.due_at) return false
  return true
})

const profileUrl = computed(() => {
  let url = '/profile'
  const params = []
  if (props.chatId) params.push(`chat_id=${encodeURIComponent(props.chatId)}`)
  if (props.fromId) params.push(`from_id=${encodeURIComponent(props.fromId)}`)
  if (params.length > 0) url += '?' + params.join('&')
  return url
})

const dashboardUrl = computed(() => {
  let url = '/dashboard'
  const params = []
  if (props.chatId) params.push(`chat_id=${encodeURIComponent(props.chatId)}`)
  if (props.fromId) params.push(`from_id=${encodeURIComponent(props.fromId)}`)
  if (params.length > 0) url += '?' + params.join('&')
  return url
})

const tasksIndexUrl = computed(() => {
  if (!props.projectId) return '#'
  let url = `/projects/${encodeURIComponent(props.projectId)}/dashboard`
  const params = []
  if (props.chatId) params.push(`chat_id=${encodeURIComponent(props.chatId)}`)
  if (props.fromId) params.push(`from_id=${encodeURIComponent(props.fromId)}`)
  if (params.length > 0) url += '?' + params.join('&')
  return url
})

const breadcrumbsItems = computed(() => {
  // Depend on localeKey to re-evaluate when translations are loaded
  localeKey.value // eslint-disable-line no-unused-expressions
  const items = [{ label: s__('Dashboard|Main'), url: dashboardUrl.value }]
  if (project.value?.title) {
    items.push({ label: project.value.title, url: tasksIndexUrl.value })
    items.push({ label: s__('TaskForm|New Task') })
  }
  return items
})

// Get viewer's Telegram user ID
const viewerUid = (() => {
  const tg = window.Telegram && window.Telegram.WebApp && window.Telegram.WebApp.initDataUnsafe
  return (tg && tg.user && tg.user.id) || fromId || null
})()

const USER_PREF_Q = gql`
  query GetUserPref($uid: BigInt!) {
    userByTelegram(telegramUserId: $uid) {
      id
      name
      username
      compactTaskForm
      personalLabelsEnabled
      templatesEnabled
      preferredLanguage
    }
  }
`

const PROJECT_Q = gql`
  query GetProject($id: ID!) {
    project(id: $id) {
      id
      title
      isPersonal
      archived
      confidentialTasksEnabled
      canManageLabels
      verticalRelationsEnabled
      templatesEnabled
      checklistsEnabled
      requiredFields
      taskCategories {
        id
        name
        workflow {
          id
          name
          statuses {
            id
            name
            position
            isInitial
          }
        }
      }
      labels {
        id
        title
        color
      }
      members {
        id
        username
        name
      }
      attachments(first: 50, projectOnly: true) {
        edges {
          node {
            id
            filename
            humanSize
          }
        }
      }
      memberships {
        id
        role
        user {
          id
          username
          name
        }
      }
    }
    priorityOptions {
      value
      label
    }
  }
`

const PROJECT_BY_CHAT = gql`
  query GetProjectByChat($chat: BigInt!) {
    projectByChat(telegramChatId: $chat) {
      id
      title
      isPersonal
      archived
      confidentialTasksEnabled
      canManageLabels
      verticalRelationsEnabled
      templatesEnabled
      checklistsEnabled
      requiredFields
      taskCategories {
        id
        name
        workflow {
          id
          name
          statuses {
            id
            name
            position
            isInitial
          }
        }
      }
      labels {
        id
        title
        color
      }
      members {
        id
        username
        name
      }
      attachments(first: 50, projectOnly: true) {
        edges {
          node {
            id
            filename
            humanSize
          }
        }
      }
      memberships {
        id
        role
        user {
          id
          username
          name
        }
      }
    }
    priorityOptions {
      value
      label
    }
  }
`

// Fetch user preferences
const { result: userPrefResult } = useQuery(
  USER_PREF_Q,
  { uid: Number(viewerUid) },
  { enabled: !!viewerUid }
)

watch(
  userPrefResult,
  data => {
    const user = data?.userByTelegram
    if (user) {
      isCompactView.value = user.compactTaskForm
      personalLabelsEnabled.value = user.personalLabelsEnabled || false
      userTemplatesEnabled.value = user.templatesEnabled || false

      // Load translations and set HTML lang attribute if user has preferred language
      const preferredLanguage = user.preferredLanguage
      if (preferredLanguage) {
        document.documentElement.lang = preferredLanguage
        loadTranslations(preferredLanguage)
        localeKey.value++ // Force re-render of computed properties that use translations
      }
    }
  },
  { immediate: true }
)

// Use projectId if available, otherwise fall back to chatId
const useProjectId = computed(() => !!props.projectId)
const {
  result: projectResult,
  loading: qLoading,
  error: qError,
} = useQuery(
  () => (useProjectId.value ? PROJECT_Q : PROJECT_BY_CHAT),
  () => (useProjectId.value ? { id: props.projectId } : { chat: Number(chatId) }),
  () => ({ enabled: !!(useProjectId.value ? props.projectId : chatId) })
)

watch(
  qLoading,
  val => {
    if (val === false) loading.value = false
  },
  { immediate: true }
)

// Remove HTML loading screen when data is loaded
watch(loading, isLoading => {
  if (!isLoading) {
    removeInitialLoading()
  }
})

watch(
  qError,
  e => {
    if (e) error.value = e.message || 'Failed to load project'
  },
  { immediate: true }
)
// Current User Query for AppHeader
const CURRENT_USER_QUERY = gql`
  query CurrentUserForHeader {
    currentUser {
      id
      name
      username
      personalLabelsEnabled
      templatesEnabled
    }
  }
`

const { result: currentUserResult } = useQuery(CURRENT_USER_QUERY)
const currentUser = computed(() => currentUserResult.value?.currentUser || null)

watch(
  currentUserResult,
  data => {
    const user = data?.currentUser

    if (user && !viewerUid) {
      personalLabelsEnabled.value = user.personalLabelsEnabled || false
      userTemplatesEnabled.value = user.templatesEnabled || false
    }
  },
  { immediate: true }
)

// Personal Labels Query
const PERSONAL_LABELS_QUERY = gql`
  query PersonalLabels {
    personalLabels {
      id
      title
      color
    }
  }
`

const {
  load: loadPersonalLabels,
  result: personalLabelsResult,
  refetch: refetchPersonalLabelsQuery,
} = useLazyQuery(
  PERSONAL_LABELS_QUERY,
  () => ({}),
  () => ({
    enabled: personalLabelsEnabled.value,
  })
)

// Watch for personal labels enabled to load personal labels
watch(
  personalLabelsEnabled,
  enabled => {
    if (enabled && !personalLabelsResult.value) {
      loadPersonalLabels()
    }
  },
  { immediate: true }
)

// Watch for personal labels query result
watch(
  personalLabelsResult,
  data => {
    if (data?.personalLabels) {
      personalLabels.value = data.personalLabels || []
    }
  },
  { immediate: true }
)

// Task Templates Queries
const USER_TEMPLATES_QUERY = gql`
  query UserTaskTemplates($userId: ID!) {
    taskTemplates(userId: $userId) {
      id
      description
      taskTitle
      taskDescription
      defaultAssigneeId
      defaultPriority
      defaultTaskCategoryId
      defaultWorkflowStatusId
      defaultParentTaskId
      defaultConfidential
      checklistData
      labels {
        id
        title
        color
      }
      personalLabels {
        id
        title
        color
      }
    }
  }
`

const PROJECT_TEMPLATES_QUERY = gql`
  query ProjectTaskTemplates($projectId: ID!) {
    taskTemplates(projectId: $projectId) {
      id
      description
      taskTitle
      taskDescription
      defaultAssigneeId
      defaultPriority
      defaultTaskCategoryId
      defaultWorkflowStatusId
      defaultParentTaskId
      defaultConfidential
      checklistData
      labels {
        id
        title
        color
      }
      personalLabels {
        id
        title
        color
      }
    }
  }
`

// Get user database ID from userPrefResult
const userDbId = computed(() => {
  return (
    userPrefResult.value?.userByTelegram?.id || currentUserResult.value?.currentUser?.id || null
  )
})

// Fetch user templates
const { result: userTemplatesResult, loading: userTemplatesLoading } = useQuery(
  USER_TEMPLATES_QUERY,
  () => ({ userId: userDbId.value }),
  () => ({
    enabled: userTemplatesEnabled.value && !!userDbId.value,
  })
)

// Fetch project templates
const { result: projectTemplatesResult, loading: projectTemplatesLoading } = useQuery(
  PROJECT_TEMPLATES_QUERY,
  () => ({ projectId: project.value?.id }),
  () => ({
    enabled: projectTemplatesEnabled.value && !!project.value?.id,
  })
)

// Watch for template results
watch(
  userTemplatesResult,
  data => {
    if (data?.taskTemplates) {
      userTemplates.value = data.taskTemplates || []
    }
  },
  { immediate: true }
)

watch(
  projectTemplatesResult,
  data => {
    if (data?.taskTemplates) {
      projectTemplates.value = data.taskTemplates || []
    }
  },
  { immediate: true }
)

watch(
  () => projectResult.value,
  data => {
    const pj = data?.project || data?.projectByChat
    if (!pj) return
    project.value = pj
    taskCategories.value = pj.taskCategories || []
    labels.value = pj.labels || []
    members.value = pj.members || []
    availableProjectAttachments.value = (pj.attachments?.edges || [])
      .map(edge => edge.node)
      .filter(Boolean)
    priorities.value = data?.priorityOptions || []
    confidentialTasksEnabled.value = pj.confidentialTasksEnabled || false
    isPersonalProject.value = pj.isPersonal === true || pj.is_personal === true
    projectArchived.value = pj.archived || false
    canManageLabels.value = pj.canManageLabels || false
    verticalRelationsEnabled.value = pj.verticalRelationsEnabled || false
    projectTemplatesEnabled.value = pj.templatesEnabled || false
    checklistsEnabled.value = pj.checklistsEnabled || false
    requiredFields.value = pj.requiredFields || []

    // Find project owner (member with 'owner' role in memberships)
    if (pj.memberships && pj.memberships.length > 0) {
      const ownerMembership = pj.memberships.find(m => m.role === 'owner')
      projectOwnerId.value = ownerMembership?.user?.id || null
      // For personal projects, auto-assign to owner (but preserve query param value if provided)
      if (isPersonalProject.value && projectOwnerId.value && !queryParams.assignee_id) {
        form.value.assignee_id = projectOwnerId.value
      }
    } else if (isPersonalProject.value && pj.members && pj.members.length > 0) {
      // Fallback: For personal projects, use the first member if memberships aren't loaded
      // as personal projects have only one member (the owner)
      projectOwnerId.value = pj.members[0]?.id || null
      // Only set assignee if it wasn't provided in query params
      if (!queryParams.assignee_id) {
        form.value.assignee_id = projectOwnerId.value
      }
    }

    if (taskCategories.value.length > 0) {
      if (!queryParams.task_category_id) {
        form.value.task_category_id = taskCategories.value[0].id
      }
    }

    // Preserve parent_task_id from query params (restore if it was cleared)
    if (queryParams.parent_task_id) {
      form.value.parent_task_id = queryParams.parent_task_id
    }
  },
  { immediate: true }
)

// Watch for verticalRelationsEnabled to ensure parent_task_id is preserved when field becomes visible
watch(verticalRelationsEnabled, enabled => {
  if (enabled && queryParams.parent_task_id && !form.value.parent_task_id) {
    form.value.parent_task_id = queryParams.parent_task_id
  }
})

// Watch for task type changes and update workflow statuses accordingly
watch(
  () => form.value.task_category_id,
  newTaskCategoryId => {
    if (!newTaskCategoryId) {
      workflowStatuses.value = []
      form.value.workflow_status_id = undefined
      return
    }

    const selectedTaskCategory = taskCategories.value.find(tt => tt.id === newTaskCategoryId)
    if (!selectedTaskCategory?.workflow?.statuses) {
      workflowStatuses.value = []
      form.value.workflow_status_id = undefined
      return
    }

    // Update statuses from the selected task type's workflow
    workflowStatuses.value = [...selectedTaskCategory.workflow.statuses].sort(
      (a, b) => a.position - b.position
    )

    // Auto-select initial status if available
    const initialStatus = workflowStatuses.value.find(s => s.isInitial)
    if (initialStatus) {
      form.value.workflow_status_id = initialStatus.id
    } else if (workflowStatuses.value.length > 0) {
      form.value.workflow_status_id = workflowStatuses.value[0].id
    } else {
      form.value.workflow_status_id = undefined
    }
  }
)

// Combine date, hour, minute into ISO datetime
function toISOFromDateHourMinute(dateStr, hour, minute) {
  if (!dateStr) return null
  const h = hour || '00'
  const m = minute || '00'
  const datetime = `${dateStr}T${h}:${m}`
  try {
    return new Date(datetime).toISOString()
  } catch {
    return null
  }
}

// Format user for display in selectboxes: shows username and name
function formatUserForSelect(user) {
  if (!user) return '—'
  const parts = []
  if (user.username) parts.push('@' + user.username)
  if (user.name) parts.push(user.name)
  if (parts.length > 0) return parts.join(' ')
  return 'User ' + user.id
}

const CREATE_TASK = gql`
  mutation CreateTask(
    $projectId: ID!
    $taskCategoryId: ID!
    $title: String!
    $description: String
    $priority: PriorityEnum
    $dueAt: ISO8601DateTime
    $assigneeId: ID
    $workflowStatusId: ID
    $labelIds: [ID!]
    $personalLabelIds: [ID!]
    $confidential: Boolean
    $whitelistedViewerIds: [ID!]
    $parentTaskId: ID
    $checklistData: JSON
    $attachmentDraftId: ID
    $existingAttachmentIds: [ID!]
    $sourceTaskTemplateId: ID
  ) {
    createTask(
      projectId: $projectId
      taskCategoryId: $taskCategoryId
      title: $title
      description: $description
      priority: $priority
      dueAt: $dueAt
      workflowStatusId: $workflowStatusId
      assigneeId: $assigneeId
      labelIds: $labelIds
      personalLabelIds: $personalLabelIds
      confidential: $confidential
      whitelistedViewerIds: $whitelistedViewerIds
      parentTaskId: $parentTaskId
      checklistData: $checklistData
      attachmentDraftId: $attachmentDraftId
      existingAttachmentIds: $existingAttachmentIds
      sourceTaskTemplateId: $sourceTaskTemplateId
    ) {
      task {
        id
        iid
        title
      }
      errors
    }
  }
`

const { mutate } = useMutation(CREATE_TASK)
const apolloClient = useApolloClient().client

// Refetch functions for labels
async function refetchProjectLabels() {
  if (!project.value?.id) return
  try {
    const { data } = await apolloClient.query({
      query: gql`
        query GetProjectLabels($id: ID!) {
          project(id: $id) {
            labels {
              id
              title
              color
            }
          }
        }
      `,
      variables: { id: project.value.id },
      fetchPolicy: 'network-only',
    })
    if (data?.project?.labels) {
      labels.value = data.project.labels || []
    }
  } catch (e) {
    console.error('Failed to refetch project labels:', e)
  }
}

async function refetchPersonalLabels() {
  if (!personalLabelsEnabled.value) return
  try {
    if (refetchPersonalLabelsQuery && typeof refetchPersonalLabelsQuery === 'function') {
      await refetchPersonalLabelsQuery()
    } else {
      await loadPersonalLabels()
    }
  } catch (e) {
    console.error('Failed to refetch personal labels:', e)
  }
}

async function showDescriptionPreview() {
  if (!form.value.description.trim()) return

  // Ensure URLs have schemas before preview
  ensureDescriptionUrlSchemas()

  // Extract task mentions from description
  const taskIds = extractTaskMentions(form.value.description)
  let taskMap = {}

  // Fetch task information if we have mentions and a project
  if (taskIds.length > 0 && project.value?.id) {
    try {
      taskMap = await fetchMentionedTasks(apolloClient, project.value.id, taskIds)
    } catch (error) {
      console.error('Failed to fetch mentioned tasks for preview:', error)
    }
  }

  // Process the description to show how it will look in Telegram
  descriptionPreviewContent.value = telegramMarkdownToHtml(form.value.description, {
    processTaskMentions: true,
    taskMap: taskMap,
    buildTaskUrl: (taskId, projectId) => {
      if (projectId) {
        return `/projects/${projectId}/tasks/${taskId}`
      }
      return `#task-${taskId}`
    },
  })
  showDescriptionPreviewModal.value = true
}

function ensureDescriptionUrlSchemas() {
  if (!form.value.description) return

  let text = form.value.description
  // Find all link patterns [text](url) and ensure URLs have schemas
  const updatedText = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, linkText, url) => {
    // Check if URL already has a schema (http://, https://, mailto:, etc.)
    const hasSchema = /^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(url.trim())
    const isRelativeUrl = url.trim().startsWith('/') || url.trim().startsWith('#')
    if (!hasSchema && !isRelativeUrl && url.trim()) {
      return `[${linkText}](https://${url.trim()})`
    }
    return match
  })

  if (updatedText !== text) {
    form.value.description = updatedText
  }
}

function resetDraftAttachments() {
  attachmentDraftId.value = null
  draftAttachments.value = []
  selectedExistingAttachmentIds.value = []
}

async function submit() {
  try {
    error.value = ''
    success.value = false
    submitting.value = true

    // Client-side validation for required fields
    if (isPriorityRequired.value && !form.value.priority) {
      error.value = s__('TaskForm|Priority is required')
      submitting.value = false
      return
    }
    if (isAssigneeRequired.value && !form.value.assignee_id) {
      error.value = s__('TaskForm|Assignee is required')
      submitting.value = false
      return
    }
    if (isDueAtRequired.value && !form.value.due_at) {
      error.value = s__('TaskForm|Due is required')
      submitting.value = false
      return
    }

    // Ensure URLs have schemas before submitting
    ensureDescriptionUrlSchemas()

    // For personal projects, always assign to the project owner
    const assigneeId = isPersonalProject.value
      ? projectOwnerId.value
      : form.value.assignee_id || undefined
    const vars = {
      projectId: project.value.id,
      taskCategoryId: form.value.task_category_id || undefined,
      title: form.value.title,
      description: form.value.description || undefined,
      priority: form.value.priority || undefined,
      dueAt: form.value.due_at || undefined,
      assigneeId: assigneeId,
      workflowStatusId: form.value.workflow_status_id || undefined,
      labelIds: form.value.label_ids.length > 0 ? form.value.label_ids : undefined,
      personalLabelIds:
        form.value.personal_label_ids && form.value.personal_label_ids.length > 0
          ? form.value.personal_label_ids
          : undefined,
      confidential: form.value.confidential || undefined,
      whitelistedViewerIds:
        form.value.confidential && form.value.whitelisted_viewer_ids.length > 0
          ? form.value.whitelisted_viewer_ids
          : undefined,
      parentTaskId: form.value.parent_task_id || undefined,
      attachmentDraftId: attachmentDraftId.value || undefined,
      existingAttachmentIds:
        selectedExistingAttachmentIds.value.length > 0
          ? selectedExistingAttachmentIds.value
          : undefined,
      sourceTaskTemplateId: selectedTemplateId.value || undefined,
      checklistData:
        checklistsEnabled.value && checklistItemsPayload().length > 0
          ? {
              title: null,
              items: checklistItemsPayload(),
            }
          : undefined,
    }
    const { data } = await mutate(vars)
    if (data?.createTask?.errors?.length) throw new Error(data.createTask.errors.join(', '))
    const created = data?.createTask?.task

    success.value = true
    if (props.embedded && project.value?.id && created?.id) {
      resetDraftAttachments()
      emit('task-created', {
        projectId: project.value.id,
        taskId: created.id,
        taskIid: created.iid,
        title: created.title,
        chatId: props.chatId,
        fromId: props.fromId,
      })
      return
    }
    if (project.value?.id && created?.iid) {
      const pid = encodeURIComponent(project.value.id)
      const tid = encodeURIComponent(created.iid)
      const chat = props.chatId ? `?chat_id=${encodeURIComponent(props.chatId)}` : ''
      const from = props.fromId
        ? `${chat ? '&' : '?'}from_id=${encodeURIComponent(props.fromId)}`
        : ''
      window.location.assign(`/projects/${pid}/tasks/${tid}${chat}${from}`)
      return
    }
    // Fallback: close WebApp if redirect URL cannot be constructed
    if (window.Telegram?.WebApp) {
      try {
        window.Telegram.WebApp.close()
      } catch {}
    }
  } catch (e) {
    error.value = e.message || 'Failed to create task'
  } finally {
    submitting.value = false
  }
}

// Computed: Show template selector if templates are enabled and at least one template exists
const showTemplateSelector = computed(() => {
  const hasUserTemplates = userTemplatesEnabled.value && userTemplates.value.length > 0
  const hasProjectTemplates = projectTemplatesEnabled.value && projectTemplates.value.length > 0
  return hasUserTemplates || hasProjectTemplates
})

// Combined templates list for easier access
const allTemplates = computed(() => {
  return [...projectTemplates.value, ...userTemplates.value]
})

// Checklist form helpers
function addChecklistItem() {
  if (!newChecklistItemTitle.value.trim()) return
  checklistForm.value.items.push({ title: newChecklistItemTitle.value.trim() })
  newChecklistItemTitle.value = ''
}

function checklistItemsPayload() {
  const items = [...checklistForm.value.items]
  const pendingTitle = newChecklistItemTitle.value.trim()

  if (pendingTitle) {
    items.push({ title: pendingTitle })
  }

  return items
    .filter(item => item.title.trim())
    .map((item, index) => ({
      title: item.title.trim(),
      position: index + 1,
    }))
}

function handleChecklistItemTab(event) {
  if (!newChecklistItemTitle.value.trim()) return

  event.preventDefault()
  addChecklistItem()
  nextTick(() => {
    if (newChecklistItemInput.value?.focus) {
      newChecklistItemInput.value.focus()
    }
  })
}

function removeChecklistItem(index) {
  checklistForm.value.items.splice(index, 1)
}

// Apply template to form
function handleTemplateChange() {
  applyTemplate()
  showTemplatePicker.value = false
}

function applyTemplate() {
  if (!selectedTemplateId.value) {
    // If no template selected, reset form to initial state (preserving query params)
    form.value = {
      title: queryParams.title || '',
      description: '',
      task_category_id: queryParams.task_category_id || null,
      label_ids: [],
      personal_label_ids: [],
      priority: undefined,
      due_at: queryParams.due_at || null,
      assignee_id: queryParams.assignee_id || undefined,
      workflow_status_id: undefined,
      confidential: false,
      whitelisted_viewer_ids: [],
      parent_task_id: queryParams.parent_task_id || undefined,
      checklist_data: null,
    }
    // Reset checklist form
    checklistForm.value = { items: [] }
    newChecklistItemTitle.value = ''
    return
  }

  const template = allTemplates.value.find(t => t.id === selectedTemplateId.value)
  if (!template) {
    return
  }

  // Check if this is a personal template (user-scoped) or project template
  const isPersonalTemplate = userTemplates.value.some(t => t.id === template.id)

  // For personal templates, preserve existing task_category_id and workflow_status_id
  // These fields should not be affected by personal templates
  // If no category is set, use the default (first category) to keep form valid
  const preservedTaskCategoryId = isPersonalTemplate
    ? form.value.task_category_id ||
      (taskCategories.value.length > 0 ? taskCategories.value[0].id : null)
    : null
  const preservedWorkflowStatusId = isPersonalTemplate ? form.value.workflow_status_id : null

  // Reset all form fields first, then apply template values
  // Convert priority from template to match the form's expected format
  // priorityOptions returns uppercase values (e.g., "MEDIUM"), template returns lowercase (e.g., "medium")
  let priorityValue = undefined
  if (template.defaultPriority) {
    const templatePriority = String(template.defaultPriority).toLowerCase()
    // Find matching priority option from the form's priorityOptions (which are uppercase)
    const matchingPriority = priorities.value.find(p => {
      const pValue = String(p.value).toLowerCase()
      return pValue === templatePriority
    })
    if (matchingPriority) {
      // Use the uppercase value from priorities array (form expects uppercase)
      priorityValue = matchingPriority.value
    } else {
      // Fallback: convert lowercase template value to uppercase to match form format
      priorityValue = templatePriority.toUpperCase()
    }
  }

  // Reset and apply template values
  form.value.title = template.taskTitle || ''
  form.value.description = template.taskDescription || ''
  form.value.assignee_id = template.defaultAssigneeId || undefined
  form.value.priority = priorityValue || undefined
  form.value.parent_task_id = template.defaultParentTaskId || undefined
  form.value.label_ids = template.labels ? template.labels.map(l => l.id) : []
  form.value.personal_label_ids = template.personalLabels
    ? template.personalLabels.map(l => l.id)
    : []
  form.value.confidential = template.defaultConfidential || false
  form.value.whitelisted_viewer_ids = []

  // Apply checklist data from template
  if (template.checklistData && checklistsEnabled.value) {
    checklistForm.value.items =
      (template.checklistData.items || []).map(item => ({
        title: item.title || '',
      })) || []
  } else {
    checklistForm.value = { items: [] }
  }
  newChecklistItemTitle.value = ''

  // Reset due date/time to empty (templates don't set due dates)
  form.value.due_date = ''
  form.value.due_hour = '00'
  form.value.due_minute = '00'

  // Only set task category and workflow status for project templates
  // Personal templates should not affect these fields - restore preserved values
  // Also, only set these fields if they are actually defined in the template
  if (!isPersonalTemplate) {
    // Set task category only if template has a default category
    if (template.defaultTaskCategoryId) {
      // Set task category first (this will trigger the watcher to update workflow statuses)
      form.value.task_category_id = template.defaultTaskCategoryId

      // Use nextTick to ensure the workflow status watcher has run before we set the template's status
      nextTick(() => {
        // Set workflow status only if template has a default status
        if (template.defaultWorkflowStatusId) {
          form.value.workflow_status_id = template.defaultWorkflowStatusId
        }
      })
    } else if (template.defaultWorkflowStatusId) {
      // If only workflow status is set (without category), we can't set it without a category
      // This shouldn't happen in practice, but handle it gracefully
      console.warn('Template has workflow status but no task category, skipping workflow status')
    }
  } else {
    // For personal templates, restore preserved values
    // Ensure task_category_id is set (required for form validation)
    // This must be set synchronously before any validation checks
    const categoryToSet =
      preservedTaskCategoryId ||
      (taskCategories.value.length > 0 ? taskCategories.value[0].id : null)
    form.value.task_category_id = categoryToSet

    // Restore workflow status (if we have a category, the watcher will update workflowStatuses)
    if (categoryToSet) {
      // The watcher on task_category_id will automatically update workflowStatuses
      // and set an initial status if available. We restore the preserved status after that.
      nextTick(() => {
        // Restore preserved workflow status, or use initial status if available
        if (preservedWorkflowStatusId) {
          // Check if the preserved status is still valid for the current category
          const isValidStatus = workflowStatuses.value.some(s => s.id === preservedWorkflowStatusId)
          if (isValidStatus) {
            form.value.workflow_status_id = preservedWorkflowStatusId
          } else {
            // Preserved status is not valid, use initial status
            const initialStatus = workflowStatuses.value.find(s => s.isInitial)
            form.value.workflow_status_id = initialStatus
              ? initialStatus.id
              : workflowStatuses.value.length > 0
                ? workflowStatuses.value[0].id
                : undefined
          }
        } else {
          // No preserved status, use initial status if available
          const initialStatus = workflowStatuses.value.find(s => s.isInitial)
          form.value.workflow_status_id = initialStatus
            ? initialStatus.id
            : workflowStatuses.value.length > 0
              ? workflowStatuses.value[0].id
              : undefined
        }
      })
    } else {
      // No category available, clear workflow status
      form.value.workflow_status_id = preservedWorkflowStatusId || undefined
    }
  }
}

onMounted(() => {
  /* query auto-runs; nothing to do */
})
</script>

<style src="../../../styles/forms.css"></style>
<style scoped>
.embedded-task-create-container {
  background: transparent;
  border-radius: 0;
  box-shadow: none;
  padding: 0;
  max-width: 100%;
  width: 100%;
}

.task-form-actions .btn-primary {
  flex: 1;
}

.task-form-actions .btn-secondary {
  white-space: nowrap;
}

.form-tip {
  margin-top: 16px;
  padding: 12px 16px;
  background-color: #f8f9fa;
  border-left: 3px solid #1d6bf3;
  border-radius: 4px;
  font-size: 13px;
  color: #555;
  line-height: 1.5;
}

.tip-link {
  color: #1d6bf3;
  text-decoration: none;
  font-weight: 500;
}

.tip-link:hover {
  text-decoration: underline;
}

.label-selector {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 4px;
}

.label-checkbox {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
}

.label-checkbox input[type='checkbox'] {
  cursor: pointer;
}

.label-badge {
  display: inline-block;
  padding: 4px 12px;
  font-size: 13px;
  font-weight: 500;
  border-radius: 4px;
  white-space: nowrap;
}

.labels-row :deep(.on-task-create-labels-form > label),
.labels-row :deep(.on-task-create-labels-form label) {
  font-size: 13px !important;
  font-weight: 500 !important;
  margin-bottom: 4px !important;
  line-height: 1.3 !important;
}

/* Modal styles */
/* Component-specific: z-index, padding, and max-width overrides */
.modal-overlay {
  z-index: 10000;
  padding: 20px;
}

.modal-content {
  max-width: 500px;
}

/* Button styles inherit from forms.css */

/* Modal color input styles (matching Show.vue) */
.modal-body input[type='color'] {
  width: 100%;
  height: 38px;
  padding: 2px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  cursor: pointer;
}

.modal-body .row {
  display: flex;
  gap: 12px;
  align-items: flex-end;
  margin-bottom: 16px;
}

.modal-body .row label {
  display: block;
  margin-bottom: 4px;
  font-size: 13px;
  font-weight: 500;
}

.modal-body .row input[type='text'] {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 14px;
}

.dashboard-task-create-project-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.template-picker {
  display: flex;
  justify-content: flex-end;
  position: relative;
  margin-bottom: 8px;
}

.template-picker-embedded {
  margin-left: auto;
  margin-bottom: 0;
}

.template-picker-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  color: #333;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  cursor: pointer;
  transition:
    background-color 0.2s,
    border-color 0.2s,
    box-shadow 0.2s;
}

.template-picker-button:hover,
.template-picker-button-active {
  background: #f5f5f5;
  border-color: #2aabee;
}

.template-picker-button:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(42, 171, 238, 0.2);
}

.template-picker-popover {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 20;
  min-width: 260px;
  padding: 8px;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
}

.template-picker-label {
  display: block;
  margin-bottom: 6px;
  color: #333;
  font-size: 13px;
  font-weight: 500;
}

.template-picker-popover select {
  width: 100%;
  margin: 0;
}

/* Alternating row colors for task form rows */
/* Count all rows, including conditional ones */
.task-info-container > .row:nth-of-type(2n) {
  background-color: #fafafa;
  padding: 12px;
  border-radius: 4px;
  margin-left: -12px;
  margin-right: -12px;
  transition: background-color 0.2s;
}

.task-info-container > .row:nth-of-type(2n + 1) {
  background-color: transparent;
}

.checklist-items-form {
  margin-top: 8px;
  padding: 12px;
  background-color: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
}

.checklist-item-row {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 4px;
}

.checklist-item-row input {
  flex: 1;
  padding: 6px 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

@media (max-width: 767px) {
  .modal-body .row input[type='text'],
  .checklist-item-row input {
    font-size: 16px;
  }
}

.btn-icon-small {
  background: none;
  border: none;
  font-size: 16px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.btn-icon-small:hover {
  background-color: #f0f0f0;
}
</style>
